import * as React from "react";
import { AlertCircle, CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { Button } from "@finster/ui";
import { sendSupportEmail } from "../api/finster";
import { trackFeedbackSubmitted } from "../analytics/product";
import { isFactSetIntentBridgeAvailable } from "../factset/factsetIntentTransport";
import { isFactSetPowerPointHost, isFactSetSpreadsheetHost } from "../factset/hostContextService";
import { isOfficeJsBypassed } from "../office/officeJsBypass";

interface SupportFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail?: string;
  slideNumber?: number;
  appEnv?: string;
  appName?: string;
  sessionId?: string;
  lastQuery?: string;
  lastResponse?: string;
}

export const SupportFeedbackDialog: React.FC<SupportFeedbackDialogProps> = ({
  open,
  onOpenChange,
  userEmail,
  slideNumber,
  appEnv,
  appName,
  sessionId,
  lastQuery,
  lastResponse,
}) => {
  const [description, setDescription] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setDescription("");
      setIsSubmitting(false);
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [open]);

  if (!open) return null;

  const isFactSetHosted =
    appName?.toLowerCase().includes("factset") ||
    isFactSetPowerPointHost() ||
    isFactSetSpreadsheetHost() ||
    (isOfficeJsBypassed() && isFactSetIntentBridgeAvailable() && !isFactSetSpreadsheetHost());
  const supportTeamName = isFactSetHosted ? "FactSet AI" : "Finster";

  const closeDialog = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedDescription = description.trim();
    if (!trimmedDescription) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const result = await sendSupportEmail({
      description: trimmedDescription,
      userEmail,
      slideNumber,
      appEnv,
      appName,
      sessionId,
      lastQuery,
      lastResponse,
    });

    setIsSubmitting(false);

    if (!result.success) {
      trackFeedbackSubmitted({
        surface: "dialog",
        feedbackType: "support",
        source: appName || "unknown",
        success: false,
      });
      setSubmitError(result.error || "Your report failed to send.");
      return;
    }

    trackFeedbackSubmitted({
      surface: "dialog",
      feedbackType: "support",
      source: appName || "unknown",
      success: true,
    });
    setSubmitSuccess(true);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={closeDialog}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-feedback-title"
        className="w-full max-w-[360px] rounded-xl border border-border bg-background p-5 text-foreground shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        {isSubmitting && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="m-0 text-sm text-muted-foreground">Sending report...</p>
          </div>
        )}

        {submitSuccess && !isSubmitting && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary" />
            <h2 id="support-feedback-title" className="m-0 text-lg font-semibold">
              Report sent
            </h2>
            <p className="m-0 text-sm text-muted-foreground">
              Thanks for sharing the details. The {supportTeamName} team will review your report.
            </p>
            <Button type="button" variant="outline" onClick={closeDialog}>
              Close
            </Button>
          </div>
        )}

        {submitError && !submitSuccess && !isSubmitting && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <XCircle className="h-10 w-10 text-destructive" />
            <h2 id="support-feedback-title" className="m-0 text-lg font-semibold">
              Failed to send
            </h2>
            <p className="m-0 text-sm text-muted-foreground">{submitError}</p>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => setSubmitError(null)}>
                Back
              </Button>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Close
              </Button>
            </div>
          </div>
        )}

        {!isSubmitting && !submitSuccess && !submitError && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="support-feedback-title" className="m-0 text-base font-semibold">
                  Share your feedback
                </h2>
                <p className="m-0 mt-4 text-sm leading-5 text-muted-foreground">
                  Report an issue with the current context or the plugin.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                aria-label="Close support dialog"
                className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <p className="m-0 text-sm font-medium text-foreground">
                {typeof slideNumber === "number" ? `Slide ${slideNumber}` : "Current context"}
              </p>
              <label htmlFor="support-feedback-description" className="text-sm font-medium">
                Description
              </label>
              <textarea
                id="support-feedback-description"
                rows={8}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="min-h-[180px] resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <Button
              type="submit"
              disabled={!description.trim()}
              className="w-full"
            >
              <AlertCircle className="h-4 w-4" aria-hidden />
              Submit
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
